import './Select.css';

import { useMemo, useRef, useLayoutEffect, useState } from 'react';
import {
  ControlItemValue,
  StaticControlItem,
} from '../../../../../types/types';
import Tooltip from '../../../Tooltip/Tooltip';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';
import Combobox from '../../../../Combobox/Combobox';
import type { GridInputBaseProps } from '../../types';
import { isEmptyValue } from '../../../../../utils/managerUtils';
import { useDefaultOptionValue } from '../../hooks/useDefaultOptionValue';

const DROPDOWN_LABEL_CLASS = '.p-dropdown-label';

type SelectProps = Omit<GridInputBaseProps<ControlItemValue>, 'value'> & {
  value: ControlItemValue | null;
  options?: StaticControlItem[];
  // Lazy-load support: parent passes isLoading and onShow to defer dynamic option fetch until first popover open
  isLoading?: boolean;
  onShow?: () => void;
  defaultValue?: ControlItemValue;
};

const Select = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  name,
  error,
  setError,
  disabled,
  options,
  isLoading = false,
  onShow,
  defaultValue,
  isColumnFilter,
}: SelectProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(() => {
    if (isEmptyValue(value)) return null;
    const option = options?.find((o) => o.value === value);
    return option?.label ?? String(value);
  }, [value, options]);

  useDefaultOptionValue(options, value, defaultValue, onChange, isColumnFilter);

  const handleOnBlur = () => {
    if (error === null) {
      setError(null);
    }
    onFocusOut?.();
  };

  const handleChange = (submitValue: ControlItemValue) => {
    onChange(submitValue);
    onTouch?.(submitValue);

    if (submitValue !== null) {
      setError(null);
    }
  };

  useLayoutEffect(() => {
    if (!wrapperRef.current || !selectedLabel) {
      setIsOverflowing(false);
      return;
    }
    const labelEl =
      wrapperRef.current.querySelector<HTMLElement>(DROPDOWN_LABEL_CLASS);
    if (!labelEl) {
      setIsOverflowing(false);
      return;
    }
    const check = () => {
      setIsOverflowing(labelEl.scrollWidth > labelEl.clientWidth);
    };

    check();
    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedLabel, value]);

  return (
    <div className="select-container">
      <div
        ref={wrapperRef}
        className="select-dropdown-tooltip-target"
        data-pr-tooltip={
          isOverflowing && selectedLabel ? selectedLabel : undefined
        }
        data-pr-position="top"
      >
        <Combobox
          value={value}
          onChange={handleChange}
          options={options}
          name={name}
          disabled={disabled}
          invalid={!!error}
          isLoading={isLoading}
          onShow={onShow}
          onBlur={handleOnBlur}
          onFocus={onFocusIn}
        />
      </div>
      <Tooltip target=".select-dropdown-tooltip-target" />
      <InputErrorMessage error={error} />
    </div>
  );
};

export default Select;
